<script setup>
import { computed, defineProps } from 'vue';
import { formatPrice, getLevelTitle } from './utils.js';

const props = defineProps({
    level: {
        type: Number,
        required: true
    },
    min: {
        type: String,
        required: true
    },
    max: {
        type: String,
        required: true
    },
    lastPrice: {
        type: Number,
        required: true
    },
    direction: {
        type: String,
        required: true
    }
});
const toNumber = (value) => {
    if (typeof value === 'string') {
        return parseFloat(value.replace('.', '').replace(',', '.'));
    }

    return value;
};

const percentage = computed(() => {
    const minNum = toNumber(props.min);
    const maxNum = toNumber(props.max);
    const lastNum = toNumber(props.lastPrice);

    if (maxNum === minNum) return 0;

    const value = ((lastNum - minNum) / (maxNum - minNum)) * 100;

    return parseFloat(value.toFixed(2));
});
</script>

<template>
    <div>
        <div class="relative min-h-24 w-4 bg-white rounded-lg mb-4 shadow-sm shadow-gray-400 text-xs text-gray-600">
            <!--Price below price target-->
            <template v-if="percentage < 0">
                <div class="absolute left-0 right-0 h-1 rounded-lg bg-black hover:scale-105 transition duration-300" :style="{ bottom: '4px' }">
                    <div class="bg-gray w-28 left-0 absolute" style="bottom: 0">
                        <div class="border-2 border-dotted border-t-gray-600 w-28 right-0 absolute">
                            <p class="absolute bottom-1 right-0 font-bold">{{ formatPrice(props.lastPrice) }}</p>
                            <p class="absolute bottom-5 right-0">{{ $t('plain_price') }}</p>
                        </div>
                    </div>
                </div>
            </template>
            <!--Price above price target-->
            <template v-else-if="percentage > 100">
                <div class="absolute left-0 right-0 h-1 rounded-lg bg-black hover:scale-105 transition duration-300" :style="{ top: '4px' }">
                    <div class="bg-gray w-28 left-0 absolute" style="bottom: 0">
                        <div class="border-2 border-t-gray-600 border-dotted w-28 right-0 absolute">
                            <p class="absolute -bottom-9 right-0 font-bold">{{ formatPrice(props.lastPrice) }}</p>
                            <p class="absolute -bottom-5 right-0">{{ $t('plain_price') }}</p>
                        </div>
                    </div>
                </div>
            </template>
            <!--Price target-->

            <div
                class="absolute top-1/2 -translate-y-1/2 h-12 w-4 bg-blue-light hover:bg-blue shadow-blue shadow-sm rounded-lg group mx-auto align-middle transition duration-300 ease-in-out"
            >
                <!-- Tooltip -->
                <div
                    class="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition duration-300 whitespace-nowrap z-50"
                >
                    {{ getLevelTitle(level) }}
                </div>

                <!-- Top -->
                <div class="border-2 border-t-gray-600 hover:border-blue border-dotted w-28 right-0 absolute">
                    <div class="h-0.5 w-28 right-0 absolute">
                        <p class="absolute -top-9 left-0 text-xxs">{{ $t('plain_top') }}</p>
                        <p class="absolute -top-5 left-0 font-bold">{{ props.max }}</p>
                    </div>
                </div>

                <!-- Price -->
                <div
                    v-if="percentage >= 0 && percentage <= 100"
                    class="absolute left-0 right-0 h-1 rounded-lg bg-black group-hover:bg-blue-light pointer-events-none"
                    :style="{ bottom: `${percentage}%` }"
                >
                    <div class="bg-gray w-28 left-0 absolute" style="bottom: 0">
                        <div class="border-2 border-t-gray-600 border-dotted w-28 right-0 absolute">
                            <p class="absolute -bottom-9 right-0 font-bold">{{ formatPrice(props.lastPrice) }}</p>
                            <p class="absolute -bottom-5 right-0">{{ $t('plain_price') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Bottom -->
                <div class="border-2 border-b-gray-600 border-dotted hover:border-blue w-28 right-0 absolute" style="bottom: 0">
                    <p class="absolute -bottom-9 left-0 font-bold">{{ props.min }}</p>
                    <p class="absolute -bottom-5 left-0 text-xxs">{{ $t('plain_bottom') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>
